import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PLAN_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Check, Crown, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import { openCheckout } from "@/lib/checkout";

export default function Plans() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const [isMock, setIsMock] = useState(true);

  const buy = async (plan) => {
    if (plan === "free") return;
    setBusy(plan);
    try {
      const { data: order } = await api.post("/payments/create-order", { plan });
      setIsMock(!!order.mock);
      await openCheckout(order, async (payload) => {
        await api.post("/payments/verify", payload);
        await refresh();
        toast.success(`${PLAN_META[plan].name} activated!`);
        nav("/app/me");
      });
    } catch (err) {
      if (err?.message !== "Payment cancelled") {
        toast.error(err?.response?.data?.detail || err?.message || "Payment failed");
      }
    } finally {
      setBusy("");
    }
  };

  const cards = [
    { key: "free", features: ["3 videos / month", "Max 45s per video", "Home feed access", "Discovery search"] },
    { key: "plus", features: ["7 videos / month", "Max 45s per video", "WhatsApp casting alerts", "In-app role notifications", "Priority in discovery"], badge: "Popular" },
    { key: "pro", features: ["20 videos / month", "Max 60s per video", "Everything in Plus", "Verified badge fast-track", "Direct DM to casting directors"], badge: "Best value" },
  ];

  return (
    <div data-testid="plans-page" className="min-h-screen pb-24">
      <TopBar subtitle="Plans"/>
      <div className="max-w-2xl mx-auto px-4 pt-6">
        <BackButton fallback="/app/me" label="Back" className="mb-4"/>
        <p className="text-xs uppercase tracking-[0.2em] text-[#DC2626]">Level up</p>
        <h1 className="font-display text-3xl font-bold mt-1">Choose your stage</h1>
        <p className="text-sm text-zinc-400 mt-2">Secure payments by Razorpay {isMock && <span className="text-[#DC2626]">· Demo mode</span>}</p>
        <div data-testid="plans-disclaimer" className="mt-3 rounded-xl border border-[#F5C462]/30 bg-[#F5C462]/5 p-3">
          <p className="text-[11px] text-zinc-300 leading-relaxed">
            <span className="text-[#F5C462] font-semibold">Note:</span> NextScene is a discovery platform. This pass does not guarantee auditions, selection, or employment.
          </p>
        </div>

        <div className="mt-6 grid gap-4">
          {cards.map(({ key, features, badge }) => {
            const meta = PLAN_META[key];
            const current = (user?.plan || "free") === key;
            return (
              <div key={key} data-testid={`plan-card-${key}`} className={`rounded-2xl border p-6 ${key === "plus" ? "border-[#DC2626] bg-gradient-to-br from-[#DC2626]/10 to-[#180D0D]" : "border-zinc-800 bg-[#180D0D]"}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      {key === "pro" ? <Sparkles className="text-[#DC2626]" size={18}/> : key === "plus" ? <Crown className="text-[#DC2626]" size={18}/> : <Users className="text-zinc-400" size={18}/>}
                      <h3 className="font-display text-xl font-bold">{meta.name}</h3>
                      {badge && <span className="text-[10px] rounded-full bg-[#DC2626]/20 text-[#DC2626] px-2 py-0.5">{badge}</span>}
                      {current && <span className="text-[10px] rounded-full bg-emerald-500/20 text-emerald-400 px-2 py-0.5">Current</span>}
                    </div>
                    <p className="mt-2 font-display text-3xl font-bold">
                      {meta.price === 0 ? "Free" : <>₹{meta.price}<span className="text-sm text-zinc-400 font-normal">/mo</span></>}
                    </p>
                  </div>
                </div>
                <ul className="mt-5 space-y-2">
                  {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={14} className="text-[#DC2626] mt-1 flex-shrink-0"/> {f}</li>
                  ))}
                </ul>
                {key !== "free" && (
                  <Button
                    data-testid={`buy-${key}`}
                    disabled={busy === key || current}
                    onClick={() => buy(key)}
                    className={`mt-5 w-full h-11 rounded-full font-semibold ${key === "plus" ? "bg-[#DC2626] hover:bg-[#EF4444] text-black" : "bg-zinc-900 border border-zinc-700 hover:border-[#DC2626]/50 text-white"}`}
                  >
                    {current ? "Active" : busy === key ? "Processing…" : `Get ${meta.name} · ₹${meta.price}`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
